import { useState, useRef, useEffect } from 'react';
import { Bell, ChevronDown, LogOut, Search } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { logout as apiLogout } from '@/lib/auth-api';
import { closeSocket } from '@/lib/socket';

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

export function Topbar() {
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  async function handleLogout() {
    try {
      await apiLogout();
    } catch {
      // Server-side logout is best-effort for bearer JWT; clear locally either way.
    }
    closeSocket();
    clear();
  }

  return (
    <header
      className="flex items-center justify-between px-5 h-[54px] gap-4 border-b border-border"
      style={{ background: 'var(--surface)' }}
    >
      <div className="relative w-[340px]">
        <Search size={14} className="absolute left-3 top-[10px] text-text-3 pointer-events-none" />
        <input
          className="w-full h-8 pl-8 pr-3 rounded-[10px] border border-border-strong bg-bg text-[13px] outline-none focus:border-accent"
          placeholder="Search agents, conversations, MCPs, workflows…"
        />
      </div>

      <div className="flex items-center gap-2.5">
        <button
          className="relative h-7 w-7 rounded-md text-text-2 hover:bg-surface-2 flex items-center justify-center"
          aria-label="Alerts"
        >
          <Bell size={15} />
          <span
            className="absolute top-1 right-1 w-[7px] h-[7px] rounded-full"
            style={{ background: 'var(--err)' }}
          />
        </button>

        <div className="flex items-center gap-2 pl-2.5 border-l border-border relative" ref={menuRef}>
          <button
            className="flex items-center gap-2 py-1 pr-1 pl-0 rounded-md hover:bg-surface-2"
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span
              className="w-7 h-7 rounded-full bg-surface-2 border border-border flex items-center justify-center text-[11px] font-semibold text-text-2"
              aria-hidden
            >
              {user ? initialsOf(user.displayName) : '·'}
            </span>
            <span className="text-[13px] font-medium">{user?.displayName ?? '—'}</span>
            <ChevronDown size={12} className="text-text-3" />
          </button>
          {menuOpen && (
            <div
              className="absolute right-0 top-[120%] min-w-[200px] rounded-xl border border-border-strong shadow-lg py-1.5 z-50"
              style={{ background: 'var(--surface)' }}
            >
              <div className="px-3 py-2 border-b border-border">
                <div className="text-[13px] font-semibold leading-tight">{user?.displayName}</div>
                <div className="text-[11.5px] text-text-3 mt-0.5">
                  {user?.role} · {user?.username}
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-2 text-[13px] hover:bg-surface-2 text-left"
              >
                <LogOut size={14} />
                Log out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
